import {
  AccountScreen,
  ComingSoonScreen,
  GetInTouchScreen,
  InviteScreen,
  SignupLayout,
  WelcomeScreen,
} from "onboarding/components";
import { useOnboardingContext } from "onboarding/OnboardingContext.tsx";
import { useTranslation } from "react-i18next";
import { useUserForSignup } from "onboarding/useUserForSignup.ts";
import { useCreateOrUpdateUserForm } from "onboarding/components/AccountScreen/useCreateOrUpdateUserForm.ts";
import { useToastContext } from "@ttc3k/trekker";
import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { AnimatedPage } from "shared/components";
import { FormProvider } from "react-hook-form";
import {
  emailRegex,
  getMultilingualString,
  handleBeaconClick,
} from "shared/utils";
import { ArrowRight } from "iconoir-react";

export const OnboardingFlow = () => {
  const { isInvited } = useOnboardingContext();
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const {
    organization,
    emailAddress,
    onEmailInputChange,
    screen,
    onScreenChange,
    invitedFrom,
    operatorName,
  } = useOnboardingContext();
  const { findUserAndNavigate, loadingUser } = useUserForSignup();
  const [isEmailError, setIsEmailError] = useState(false);

  const {
    form,
    onSubmit,
    defaultValues,
    loading: loadingCreateOrUpdate,
  } = useCreateOrUpdateUserForm({
    onSuccess: (passwordResetUrl: string) => {
      form.reset(defaultValues);
      window.location.href = passwordResetUrl;
    },
    onError: () => {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("onboarding:FORM.ERROR"),
      });
    },
  });
  const formId = "create-or-update-user-form";

  const getCurrentScreen = () => {
    /* Conditionally set props based on the Invited and Cold Signup flows */
    const inviteScreenProps = {
      title: isInvited ? t("onboarding:EMAIL_SCREEN.INVITED.TITLE") : "",
      description: isInvited
        ? t("onboarding:EMAIL_SCREEN.INVITED.DESCRIPTION", {
            organizationName:
              invitedFrom === "ORGANIZATION"
                ? getMultilingualString(organization?.name ?? {})
                : operatorName,
          })
        : t("onboarding:EMAIL_SCREEN.COLD_SIGNUP.DESCRIPTION"),
      inputProps: isInvited
        ? { disabled: true }
        : {
            onKeyUp: (e: React.KeyboardEvent) =>
              e.code === "Enter" ? handleNext() : null,
            onChange: onEmailInputChange,
            disabled: loadingUser,
            ...(isEmailError
              ? {
                  "data-invalid": true,
                  "aria-invalid": true,
                  hint: `${t("core:FORM_ERRORS.INVALID")}${t("onboarding:EMAIL_SCREEN.COLD_SIGNUP.EMAIL")}`,
                }
              : {}),
          },
    };

    switch (screen) {
      case "invite":
        return <InviteScreen {...inviteScreenProps} />;
      case "welcome":
        return <WelcomeScreen />;
      case "create":
        return <AccountScreen />;
      case "get-in-touch":
        return <GetInTouchScreen />;
      case "coming-soon":
        return <ComingSoonScreen />;
    }
  };

  const handleNext = async () => {
    if (isInvited) {
      if (screen === "invite") {
        await findUserAndNavigate();
      } else {
        onScreenChange("create");
      }
    } else {
      if (screen === "welcome") {
        onScreenChange("create");
      } else if (emailAddress.match(emailRegex)) {
        setIsEmailError(false);
        form.setValue("email", emailAddress);
        await findUserAndNavigate();
      } else {
        setIsEmailError(true);
      }
    }
    if (screen === "get-in-touch") {
      handleBeaconClick({ email: emailAddress });
    }
    if (screen === "coming-soon") {
      if (organization?.contact?.links?.website) {
        window.location.href = organization.contact.links.website;
      } else {
        window.history.go(-1);
      }
    }
  };

  return (
    <AnimatePresence mode={"wait"}>
      <AnimatedPage key={screen} initial={"enter"}>
        <FormProvider {...form}>
          <form onSubmit={onSubmit} id={formId}>
            <SignupLayout
              onNextClick={screen !== "create" ? handleNext : null}
              buttonProps={{
                isIconRightAligned: true,
                Icon:
                  !(screen === "get-in-touch" || screen === "coming-soon") &&
                  isInvited &&
                  ArrowRight,
                type: screen === "create" ? "submit" : "button",
                visual: screen === "get-in-touch" ? "accent.purple" : "primary",
                isLoading:
                  (screen === "invite" && loadingUser) ||
                  (screen === "create" && loadingCreateOrUpdate),
                children:
                  screen === "get-in-touch"
                    ? t("core:GET_IN_TOUCH")
                    : screen === "coming-soon"
                      ? t("core:BACK_TO_SITE")
                      : screen === "invite" && isInvited
                        ? t("core:NEXT")
                        : t("core:CONTINUE"),
              }}
            >
              {getCurrentScreen()}
            </SignupLayout>
          </form>
        </FormProvider>
      </AnimatedPage>
    </AnimatePresence>
  );
};
